import { FC, useRef, useState } from "react"
import uploadIcon from "assets/svg/upload.svg"
import {
  DEFAULT_ALLOWED_EXTENSIONS,
  extensionsToAccept,
} from "utils/file-upload"

/**
 * Usage:
 *   const { isProcessing, processFile } = useFileUpload({ ... })
 *
 *   <DropZone
 *     onFileSelected={processFile}
 *     isProcessing={isProcessing}
 *     allowedExtensions={["csv", "xlsx"]}
 *     acceptedFormatsLabel="CSV or XLSX"
 *     maxFileSizeMB={50}
 *   />
 */

export interface DropZoneProps {
  onFileSelected: (file: File) => void
  isProcessing: boolean
  allowedExtensions?: string[]
  acceptedFormatsLabel?: string
  maxFileSizeMB?: number
}

const DropZone: FC<DropZoneProps> = ({
  onFileSelected,
  isProcessing,
  allowedExtensions = DEFAULT_ALLOWED_EXTENSIONS,
  acceptedFormatsLabel = "CSV, XLS, or XLSX",
  maxFileSizeMB = 50,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isDragOver, setIsDragOver] = useState(false)

  const dragCounter = useRef(0)

  const handleBrowse = () => {
    if (isProcessing) return
    fileInputRef.current?.click()
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) onFileSelected(file)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (isProcessing) return
    dragCounter.current += 1
    setIsDragOver(true)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (isProcessing) return
    dragCounter.current -= 1
    if (dragCounter.current <= 0) {
      dragCounter.current = 0
      setIsDragOver(false)
    }
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current = 0
    setIsDragOver(false)
    if (isProcessing) return
    const file = e.dataTransfer.files?.[0]
    if (file) onFileSelected(file)
  }

  return (
    <div
      className={`border-2 border-dashed rounded-lg flex flex-col justify-center items-center py-7 gap-1 mb-2 transition-colors ${
        isProcessing ? "cursor-not-allowed opacity-60" : "cursor-pointer"
      } ${isDragOver ? "border-[#004AF5] bg-[#EFF4FF]" : "border-[#E2E8F0]"}`}
      onClick={handleBrowse}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <span className="border border-[#E2E8F0] rounded-lg p-2.5 bg-white pointer-events-none">
        <img
          width={22}
          height={22}
          alt="upload"
          src={uploadIcon}
        />
      </span>
      <p className="font-medium pointer-events-none">
        {isDragOver ? (
          "Drop file here"
        ) : (
          <>
            Drag and drop files or{" "}
            <span className="text-[#004AF5]">browse</span>
          </>
        )}
      </p>
      <p className="text-sm text-[#62748E] pointer-events-none">
        {acceptedFormatsLabel} · Max {maxFileSizeMB}MB
      </p>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleInputChange}
        accept={extensionsToAccept(allowedExtensions)}
        className="hidden"
      />
    </div>
  )
}

export default DropZone
